import { JSX, lazy, LazyExoticComponent, MemoExoticComponent, ReactNode } from 'react';
import { featuresFlags, isExtCharts } from './helpers/helpers';

// Routy se řídí runtime feature profilem (brand podle domény), stejně jako
// sidebar nav (useNav.tsx) a layout — ne build-time __ENABLED_ROUTES__.
const enabledRoutes = featuresFlags.routes;

const routes: {
  path: string,
  name: string,
  exact?: boolean,
  element?: LazyExoticComponent<MemoExoticComponent<() => ReactNode>> | LazyExoticComponent<() => JSX.Element>
}[] = [];

// ---- MAIN ROUTES
if (enabledRoutes.dashboard) {
  const Dashboard = lazy(() => import('./views/dashboard/Dashboard'));
  routes.push({ path: 'dashboard', name: 'Dashboard', element: Dashboard });
}

if (enabledRoutes.map) {
  const Map = lazy(() => import('./views/map/Map'));
  routes.push({ path: 'map', name: 'Map', element: Map });
}

if (enabledRoutes.charts) {
  const Charts = lazy(() => import('./views/charts/ChartsWrapper'));
  const path = isExtCharts ? "/" : "/charts";
  routes.push({ path, name: 'Charts', element: Charts });
}

if (enabledRoutes.export) {
  const Export = lazy(() => import('./views/export/Export'));
  routes.push({ path: 'export', name: 'Export', element: Export });
}

if (enabledRoutes.reports) {
  const Reports = lazy(() => import('./views/reports/Reports'));
  routes.push({ path: 'reports', name: 'Reports', element: Reports });
}

if (enabledRoutes.users) {
  const Users = lazy(() => import('./views/users/Users'));
  routes.push({ path: 'users', name: 'Users', element: Users });
}

if (enabledRoutes.companies) {
  const Companies = lazy(() => import('./views/companies/Companies'));
  routes.push({ path: 'companies', name: 'Companies', element: Companies });
}

if (enabledRoutes.constructions) {
  const Constructions = lazy(() => import('./views/constructions/Constructions'));
  routes.push({ path: 'constructions', name: 'Constructions', element: Constructions });
}

if (enabledRoutes.devices) {
  const Devices = lazy(() => import('./views/devices/Devices'));
  routes.push({ path: 'devices', name: 'Devices', element: Devices });
}

if (enabledRoutes.notifications) {
  const Notifications = lazy(() => import('./views/notifications/Notifications'));
  routes.push({ path: 'notifications', name: 'Notifications', element: Notifications });
}

if (enabledRoutes.userId) {
  const User = lazy(() => import('./views/user/User'));
  routes.push({ path: 'user/:id', name: 'User', element: User });
}

// ---- DYNASENCE ROUTES
if (enabledRoutes.dynasenceDashboard) {
  const DynaBoard = lazy(() => import('./views/dyna-board/DynaBoardWrapper'));
  routes.push({ path: 'dynasence-dashboard', name: 'Dynasence', element: DynaBoard });
}

// ---- APPLICATIONS
if (enabledRoutes.constructionView) {
  const ConstructionView = lazy(() => import('./views/construction-view/ConstructionViewWrapper'));
  routes.push({ path: 'construction-view', name: 'ConstructionView', element: ConstructionView });
}

if (enabledRoutes.bttTerminal) {
  const BTTerminal = lazy(() => import('./views/terminal/BTTerminal'));
  routes.push({ path: 'btt-terminal', name: 'Bluetooth Terminal', element: BTTerminal });
}

if (enabledRoutes.bridgeAnimation) {
  const BridgeAnimation = lazy(() => import('./views/applications/bridge-animation/BridgeAnimationRuntime'));
  routes.push({ path: 'bridge-animation', name: 'Bridge Animation', element: BridgeAnimation });
}

if (enabledRoutes.geoSnakeAnimation) {
  const GeoSnakeAnimation = lazy(() => import('./views/applications/geosnake-animation/GeoSnakeAnimationWrapper'));
  routes.push({ path: 'geosnake', name: 'Geo Snake Animation', element: GeoSnakeAnimation });

  if (isExtCharts) {
    const GeoSnakeFrame = lazy(() => import('./views/applications/geosnake-animation/single-frame/GeosnakeSingleFrame'));
    routes.push({ path: 'geosnake-frame', name: 'Geo Snake Single Frame', element: GeoSnakeFrame });
  }
}

if (enabledRoutes.dynalyzer) {
  const Dynalyzer = lazy(() => import('./views/applications/dynalyzer/Dynalyzer'));
  routes.push({ path: 'dynalyzer', name: 'Dynalyzer', element: Dynalyzer });
}

// TODO - make these a standalone application in DB and API instead of this hardcoded mess
if (enabledRoutes.monpasAnalyzer) {
  const MonpasAnalyzer = lazy(() => import("./views/applications/monpas-analyzer/MonpasAnalyzer"));
  routes.push({ path: 'monpas-analyzer', name: 'Monpas Analyzer', element: MonpasAnalyzer });
}
if (enabledRoutes.nordecJihlavaArena) {
  const NordecJihlavaIframe = lazy(() => import("./views/applications/nordec-jihlava-arena/NordecJihlavaArenaIframe"));
  routes.push({ path: 'jihlava-arena', name: 'Jihlava Arena', element: NordecJihlavaIframe });
}

if (enabledRoutes.senzoracek) {
  const Senzoracek = lazy(() => import("./views/applications/senzoracek/Senzoracek"));
  routes.push({ path: 'models', name: 'Models', element: Senzoracek });
}

// ---- INTERNAL
if (enabledRoutes.deviceAlertWatcher) {
  const DeviceAlertWatcher = lazy(() => import('./views/devices-alert-watcher/index'));
  routes.push({ path: 'device-alert-watcher', name: 'Device Alert States', element: DeviceAlertWatcher });
}

if (enabledRoutes.dynmgmt) {
  const Dynmgmt = lazy(() => import('./views/applications/dynmgmt/Dynmgmt'));
  routes.push({ path: 'dynmgmt', name: 'Dynmgmt', element: Dynmgmt });
}

if (enabledRoutes.tempCompensation) {
  const TempCompensation = lazy(() => import('./views/applications/temp-compensation/TempCompensation'));
  routes.push({ path: 'temp-compensation', name: 'Temperature Compensation', element: TempCompensation });
}

if (enabledRoutes.markers) {
  const MarkersOverview = lazy(() => import('./views/markers/MarkersOverview'));
  routes.push({ path: 'markers', name: 'Markers', element: MarkersOverview });
}

if (enabledRoutes.alerts) {
  const Alerts = lazy(() => import('./views/alerts/Alerts'));
  routes.push({ path: 'alerts', name: 'Alerts', element: Alerts });
}

// ---- ADMINISTRATION (GlobalAdmin-only panel; role gate lives in the view)
if (enabledRoutes.administration) {
  const Administration = lazy(() => import('./views/administration/Administration'));
  routes.push({ path: 'administration', name: 'Administration', element: Administration });
}

export default routes;
